/**
 * API client — axios instance, interceptors, token storage helpers.
 *
 * Security: access token in memory only (not localStorage — XSS-safe).
 * Refresh token in httpOnly cookie (inaccessible to JS).
 */
import axios from 'axios';

export const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  `${window.location.protocol}//${window.location.hostname}:8000`;

// ── Token (in-memory only — no localStorage) ──
let _accessToken = null;

export const getAccess = () => _accessToken;
export const saveAccess = (token) => { _accessToken = token; };
export const clearAccess = () => { _accessToken = null; };

// ── Impersonation (admin "view as user") ──
// While impersonating, the admin's own access token is kept aside and the
// silent-refresh-on-401 path is disabled: the refresh cookie belongs to the
// admin session, so a silent refresh would quietly restore admin privileges.
let _impersonating = false;
let _adminAccessToken = null;

export const isImpersonating = () => _impersonating;

export const startImpersonation = (token) => {
  if (!_impersonating) _adminAccessToken = _accessToken;
  _impersonating = true;
  _accessToken = token;
};

export const stopImpersonation = () => {
  if (!_impersonating) return;
  _impersonating = false;
  _accessToken = _adminAccessToken;
  _adminAccessToken = null;
  window.dispatchEvent(new Event('auth:impersonation-ended'));
};

export const onImpersonationEnded = (cb) => {
  const handler = () => cb();
  window.addEventListener('auth:impersonation-ended', handler);
  return () => window.removeEventListener('auth:impersonation-ended', handler);
};

export const clearAuth = () => {
  clearAccess();
  _impersonating = false;
  _adminAccessToken = null;
  window.dispatchEvent(new Event('auth:lost'));
};

export const onAuthLost = (cb) => {
  const handler = () => cb();
  window.addEventListener('auth:lost', handler);
  return () => window.removeEventListener('auth:lost', handler);
};

export const getAuth = () => {
  const t = getAccess();
  return t ? { access: t } : null;
};

// ── Axios instance ──
const api = axios.create({ baseURL: API_BASE_URL, withCredentials: true });

api.interceptors.request.use((config) => {
  const access = getAccess();
  if (access) {
    config.headers.Authorization = `Bearer ${access}`;
  }
  return config;
});

// ── Refresh on 401 ──
let refreshPromise = null;

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const req = error.config;
    if (error.response?.status === 401 && _impersonating) {
      // The impersonated token expired. Never silently refresh here —
      // the refresh cookie is the admin's. Drop back to the admin session.
      stopImpersonation();
      return Promise.reject(error);
    }
    if (error.response?.status === 401 && !req._retry) {
      req._retry = true;
      try {
        if (!refreshPromise) {
          refreshPromise = axios
            .post(`${API_BASE_URL}/refresh`, null, { withCredentials: true })
            .then((r) => {
              saveAccess(r.data.access_token);
              return r.data.access_token;
            })
            .finally(() => { refreshPromise = null; });
        }
        const newAccess = await refreshPromise;
        req.headers.Authorization = `Bearer ${newAccess}`;
        return api(req);
      } catch {
        clearAuth();
      }
    }
    return Promise.reject(error);
  }
);

// ── Silent refresh on page load ──
export async function initAuth() {
  if (getAccess()) return getAuth();
  try {
    const r = await axios.post(`${API_BASE_URL}/refresh`, null, { withCredentials: true });
    saveAccess(r.data.access_token);
    window.dispatchEvent(new Event('auth:restored'));
    return { access: r.data.access_token };
  } catch {
    return null;
  }
}

export default api;
